import "./i18n";
import React, { Fragment, useMemo, useState, useCallback, useEffect } from "react";
import styles from "./UserMenu.module.scss";
import globalStyles from "@whatslly/ui-components/v2/globalStyles";
import { CtaLink } from "@whatslly/ui-components/CtaLink/CtaLink";
import classnames from "classnames";
import { useToast } from "@whatslly/ui-components/v2/Toaster/ToastContainer";
import copy from "copy-to-clipboard";
import { useTranslation } from "react-i18next";
import { Svg } from "@whatslly/ui-components/v2/BaseElements/Svg";
import { useDependencyInjector } from "@whatslly/commons/di";
import { observer } from "mobx-react-lite";
import type { IUserStore } from "@whatslly/ui-components/services/UserStore";
import { InjectUserStore } from "@whatslly/ui-components/services/UserStore";
import { Separator } from "@whatslly/ui-components/Separator";
import type { UpdateUserBodyType } from "@whatslly/types/UpdateUserBody";
import { SvgV2 } from "../v2/BaseElements/SvgV2";
import { Tooltip } from "../Tooltip";
import { Switch } from "../v2/BaseElements/Switch";
import { modalService } from "../modals";
import { EditUserModal } from "./EditUserModal/EditUserModal";
import { TERMS_CONDITIONS_TUVIS_URL } from "@whatslly/commons/constants";
import Toast from "../v2/Toaster/Toast";

export type MenuItemType = {
  text: React.ReactNode;
  link?: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  testId?: string;
  separator?: boolean;
  subtitle?: boolean | undefined;
  status?: boolean; // true for on, false for off
  statusIsDisabled?: boolean;
  center?: boolean;
  child?: React.ReactNode;
};

type UserMenuProps = {
  openUserMenu?: boolean;
  menuItems?: MenuItemType[];
  additionalMetaData?: React.ReactNode;
  version?: string;
  updateUser?: (data: UpdateUserBodyType) => Promise<void>;
  onLogout?: () => void;
  replaceOnLogout?: boolean;
  /** When true, edit-user modal uses Electron main-process `WA_NOTIFICATION_SOUND_*` commands (same contract as `TopBar` `isDesktop`). */
  isDesktop?: boolean;
};

export const UserMenu: React.FC<UserMenuProps> = observer(
  ({ openUserMenu, menuItems: _menuItems, version, updateUser, onLogout, replaceOnLogout, isDesktop }) => {
    const { triggerToast } = useToast();
    const { t, i18n } = useTranslation();
    const userStore = useDependencyInjector<IUserStore>(InjectUserStore);
    const [userMenuIsOpen, setUserMenuIsOpen] = useState(false);
    const user = userStore.user;

    useEffect(() => {
      if (openUserMenu) {
        setUserMenuIsOpen(true);
      }
    }, [openUserMenu]);

    const menuItemsLength = useMemo(() => {
      return _menuItems?.filter(Boolean).length;
    }, [_menuItems?.length]);

    const menuItems: MenuItemType[] = useMemo(() => {
      const items = [...(_menuItems ?? [])];

      if (updateUser) {
        const firstSeperator = items.filter(Boolean).findIndex((item) => item.separator) + 1;
        // firstName and lastName are new fields, allow editing them only if they are present
        if (user?.firstName && user.lastName && !user.hide_edit_user) {
          items.splice(firstSeperator, 0, {
            text: t("edit_user.title"),
            separator: true,
            testId: "open-edit-user-modal-content",
            onClick: async () => {
              if (!user) return;
              const update = await modalService.openModal(EditUserModal, { user, isDesktop }).promise;
              if (!update) return;
              await updateUser(update);
              modalService.openModal(Toast, { text: t("edit_user.success"), type: "regular" });
            },
          });
        }
      }
      return [
        ...items,
        user && !user.hide_logout
          ? {
              text: t("settings_logout"),
              onClick: async () => {
                !replaceOnLogout && (await userStore.logout());
                onLogout?.();
              },
              subtitle: true,
              testId: "top-bar-logout",
            }
          : (undefined as unknown as MenuItemType),
      ].filter(Boolean);
    }, [menuItemsLength, updateUser, onLogout, t, user, userStore, replaceOnLogout, isDesktop]);

    const settingItems = useMemo(() => {
      return [
        {
          text: t("settings_about"),

          link: "https://tuvis.com/about/?utm_source=widget",
        },
        {
          text: t("settings_privacy"),

          link: "https://tuvis.com/privacy/?utm_source=widget",
        },
        {
          text: t("settings_terms"),

          link: `${TERMS_CONDITIONS_TUVIS_URL}?utm_source=widget`,
        },
      ];
    }, [t]);

    const instanceUrl = useMemo(() => {
      if (!user?.base || user?.hideCrmLinks) {
        return "";
      }
      try {
        const urlObj = new URL(user.base);
        return urlObj.origin;
      } catch {
        return "";
      }
    }, [user?.base, user?.hideCrmLinks]);

    const copyToClipboard = useCallback(
      (event: React.MouseEvent<HTMLDivElement>) => {
        if (!user) {
          return;
        }

        const targetElement = event.target as HTMLElement;
        if (targetElement?.tagName !== "A") {
          triggerToast({
            text: t("copied_to_clipboard"),
          });
        }

        const txt = [
          ["version", version],
          ["_id", user._id],
          ["orgId", user.organizationId],
          ["userId", user.userId],
          ["base", instanceUrl],
        ]
          .filter(([, value]) => value)
          .map(([key, value]) => `${key}: ${value}`)
          .join("\n");
        return copy(txt);
      },
      [user, version, instanceUrl, triggerToast, t]
    );

    return (
      <div
        className={styles.mainWrapper}
        tabIndex={2}
        onBlur={(event: React.FocusEvent<HTMLDivElement>) => {
          if (event.relatedTarget?.tagName === "A" && (event.relatedTarget as HTMLAnchorElement)?.href) {
            return;
          }
          setUserMenuIsOpen(false);
        }}
      >
        <div
          data-testid="top-bar-user-menu"
          className={classnames(styles.topBarHeaderProfileContainer, globalStyles["bc-neutral-secondary"], globalStyles["color-main-primary"])}
          onClick={() => {
            setUserMenuIsOpen(!userMenuIsOpen);
          }}
        >
          <Svg iconName={`settings`} className={styles.menuBurger} />

          <div className={classnames(styles.topBarHeaderProfileIcon)} data-testid="username-initial">
            {userStore.user?.name ? userStore.user.name[0]?.toUpperCase() : <Svg iconName={`action-new-lead-contact`} />}
          </div>
          {userMenuIsOpen && (
            <div className={styles.userMenu} data-testid="user-menu">
              <div className={classnames(styles.menuContainer, styles[i18n.language.substring(0, 2)])}>
                <div className={styles.linksContainer}>
                  {menuItems.map((menuItem, index) => (
                    <Fragment key={index}>
                      <CtaLink
                        skin={`anchor`}
                        onClick={menuItem.onClick}
                        href={menuItem.link}
                        className={classnames(styles.linkWrapper, styles.link, {
                          [styles.center]: menuItem.status !== undefined || menuItem.center,
                          [styles.subtitle]: menuItem.subtitle,
                        })}
                      >
                        <div className={styles.textContainer} data-testid={menuItem.testId}>
                          <span className={styles.itemName}>{menuItem.text}</span>
                          {menuItem.status !== undefined && <Switch checked={menuItem.status} disabled={menuItem.statusIsDisabled} scale={"small"} />}
                          {menuItem.child}
                        </div>
                      </CtaLink>
                      {menuItem.separator && <Separator className={styles.separator} />}
                    </Fragment>
                  ))}
                </div>
                <div className={classnames(styles.subMenuBottom, { [styles.loggedOut]: !user })}>
                  {user && (
                    <div className={styles.userDataAndCopyContainer} onClick={(event) => copyToClipboard(event)}>
                      <div className={styles.userData}>
                        {version && (
                          <span className={styles.subMenuBottomItem}>
                            {t("VERSION_PREFIX")}
                            {version}
                          </span>
                        )}
                        {user._id && <span className={styles.subMenuBottomItem}>{`_id: ${user._id}`}</span>}
                        {user.organizationId && <span className={styles.subMenuBottomItem}>{`${t("ORG_ID_PREFIX")}${user.organizationId}`}</span>}
                        {user.userId && <span className={styles.subMenuBottomItem}>{`${t("USER_ID_PREFIX")}${user.userId}`}</span>}
                        {instanceUrl && (
                          <a className={styles.subMenuBottomItem} href={instanceUrl} target="sfdc">
                            {instanceUrl}
                          </a>
                        )}
                      </div>
                      <div className={styles.copyUserDataWrapper}>
                        <div className={styles.copyUserDataContainer}>
                          <SvgV2 iconName={`copy-text-icon`} />
                        </div>
                      </div>
                    </div>
                  )}

                  <div className={styles.settingsContainer}>
                    {settingItems.map((item, key) => (
                      <Fragment key={key}>
                        {Boolean(key) && <span className={styles.settingSeperator} />}
                        <Tooltip value={<span>{item.text}</span>} key={`${key}_${item.text}`} disabled={false}>
                          <a href={item.link} target={"_blank"} className={styles.setting}>
                            {item.text}
                          </a>
                        </Tooltip>
                      </Fragment>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
);
